import {
  Component,
  signal,
  computed,
  effect,
  inject,
  input,
  output,
  ViewChild,
} from '@angular/core';
import { ImageCropperComponent, OutputFormat } from 'ngx-image-cropper';
import { I18nService } from '../../services/i18n.service';
import { UiService } from '../../services/ui.service';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { FooterComponent } from '../footer/footer';
import { NavButtonComponent } from '../nav-button/nav-button';
import { blobToBase64, processImage } from '../../utilities/global.util';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';

@Component({
  selector: 'app-image-crop',
  imports: [NzIconModule, ImageCropperComponent, FooterComponent, NavButtonComponent],
  templateUrl: './image-crop.html',
  styleUrls: ['./image-crop.scss'],
})
export class ImageCropComponent {
  fileEvent = input<any>(null);
  mode = input<'photo' | 'scan'>('photo');

  imageSaved = output<string>();
  navBack = output<void>();
  clickExit = output<void>();

  fileType = signal<OutputFormat>('png');
  croppedImage = signal<string>(GLOBAL_CONSTANT.EMPTY_STRING);
  imageLoaded = signal<boolean>(false);

  @ViewChild(ImageCropperComponent) imageCropper!: ImageCropperComponent;

  public i18n = inject(I18nService);
  private ui = inject(UiService);

  readonly isMobile = computed(() => this.ui.IsMobile());
  readonly isDesktop = computed(() => this.ui.IsDesktop());

  // Meant for this component only so no required to create utitly
  readonly fileEffect = effect(() => {
    const event = this.fileEvent();
    if (event) {
      const files: File[] = Array.from(event.target.files);
      const type = files[0] ? files[0].type.split('/')[1] : 'png';
      const validType: OutputFormat = ['png', 'jpeg', 'webp'].includes(type)
        ? (type as OutputFormat)
        : 'png';
      this.fileType.set(validType);
    }
  });

  onImageLoaded() {
    this.imageLoaded.set(true);
  }

  async onImageCropped(event: any) {
    let base64String: string | null = null;

    // Check for base64 first
    if (event && event.base64) {
      base64String = event.base64;
    }
    // Otherwise check for objectUrl or blob
    else if (event && (event.objectUrl || event.blob)) {
      if (event.blob) {
        base64String = await blobToBase64(event.blob);
      }
    }

    if (base64String) {
      const compressed = await processImage(base64String);
      if (compressed) {
        this.croppedImage.set(compressed);
      }
    } else {
      console.warn('No base64 or blob data in cropped event');
    }
  }

  async obtainCroppedImage() {
    // Wait for image to be loaded
    if (!this.imageLoaded()) {
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    // Try to get the cropped image
    try {
      // crop() is now async, need to await it
      const image = await this.imageCropper.crop();

      let base64String: string | null = null;

      // Check for base64 first
      if (image && image.base64) {
        base64String = image.base64;
      }
      // Otherwise check for blob and convert it
      else if (image && image.blob) {
        base64String = await blobToBase64(image.blob);
      }

      if (!base64String) {
        return;
      }

      const compressed = await processImage(base64String);
      if (!compressed) return;
      this.croppedImage.set(compressed);
    } catch (error) {
      console.error('Error during crop:', error);
    }
  }

  async confirmImageCrop() {
    // Always try to get the latest crop if croppedImage is empty
    if (!this.croppedImage()) {
      await this.obtainCroppedImage();
    }

    // Double-check we have a valid cropped image before emitting
    const finalImage = this.croppedImage();
    if (finalImage) {
      this.imageSaved.emit(finalImage);
    } else {
      console.error('No cropped image available to save');
    }
  }
}
